import { Check, RefreshCw, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import Spinner from "components/Spinner";
import AdminManagerRequestCard from "components/admin/AdminManagerRequestCard";
import {
  approveApprovalRequest,
  getApprovalRequests,
  rejectApprovalRequest,
} from "lib/api/approvalRequests";
import {
  approveManagerCreationRequest,
  getManagerCreationRequests,
  rejectManagerCreationRequest,
} from "lib/api/roleAdmin";
import { requestGlobalNotificationsRefresh } from "lib/notifications/globalNotifications";
import { useAppStore } from "lib/store/useAppStore";
import { formatKoreanDateTime } from "lib/utils/koreanDateTime";

function getRequestPayload(request) {
  return request?.payload && typeof request.payload === "object"
    ? request.payload
    : {};
}

function getRequestName(request) {
  const payload = getRequestPayload(request);
  return payload.user_name || payload.userName || "이름 미입력";
}

function getRequestEmail(request) {
  const payload = getRequestPayload(request);
  return (
    payload.email ||
    request?.requester_email ||
    request?.requesterEmail ||
    "-"
  );
}

function formatRequestDate(value) {
  return formatKoreanDateTime(value, { year: "numeric" });
}

export default function AdminSignupRequestsPage() {
  const showAlert = useAppStore((state) => state.showAlert);
  const isMaster = useAppStore((state) => state.user?.role === "master");
  const [requests, setRequests] = useState([]);
  const [managerRequests, setManagerRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [processingId, setProcessingId] = useState("");
  const [managerProcessingId, setManagerProcessingId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [managerRequestError, setManagerRequestError] = useState("");

  const loadRequests = useCallback(async () => {
    setIsLoading(true);
    const [result, managerResult] = await Promise.all([
      getApprovalRequests({
        requestType: "signup_approval",
        status: "pending",
      }),
      isMaster
        ? getManagerCreationRequests({ status: "pending" })
        : Promise.resolve(null),
    ]);
    setIsLoading(false);

    if (!result.success) {
      setRequests([]);
      setErrorMessage(
        result.message || "계정 생성 요청을 불러오지 못했습니다.",
      );
    } else {
      setRequests(
        Array.isArray(result.data?.requests) ? result.data.requests : [],
      );
      setErrorMessage("");
    }

    if (!isMaster) {
      setManagerRequests([]);
      setManagerRequestError("");
    } else if (!managerResult?.success) {
      setManagerRequests([]);
      setManagerRequestError(
        managerResult?.message || "매니저 생성 요청을 불러오지 못했습니다.",
      );
    } else {
      setManagerRequests(
        Array.isArray(managerResult.data?.requests)
          ? managerResult.data.requests
          : [],
      );
      setManagerRequestError("");
    }
  }, [isMaster]);

  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);

  const handleReview = async (request, action) => {
    if (!request?.id || processingId) return;

    if (
      action === "reject" &&
      !window.confirm(
        `${getRequestName(request)}님의 계정 생성 요청을 거절하시겠습니까?`,
      )
    ) {
      return;
    }

    setProcessingId(request.id);
    const result =
      action === "approve"
        ? await approveApprovalRequest(request.id)
        : await rejectApprovalRequest(request.id);
    setProcessingId("");

    showAlert({
      text:
        result.message ||
        (action === "approve"
          ? "계정 생성 요청을 승인했습니다."
          : "계정 생성 요청을 거절했습니다."),
      success: result.success,
      autoClose: true,
    });

    if (result.success) {
      requestGlobalNotificationsRefresh();
      await loadRequests();
    }
  };

  const handleReviewManagerRequest = async (id, action) => {
    if (!id || managerProcessingId) return;

    setManagerProcessingId(id);
    const result =
      action === "approve"
        ? await approveManagerCreationRequest(id)
        : await rejectManagerCreationRequest(id);
    setManagerProcessingId("");

    showAlert({
      text: result.message || "매니저 생성 요청을 처리했습니다.",
      success: result.success,
      autoClose: true,
    });

    if (result.success) {
      requestGlobalNotificationsRefresh();
      await loadRequests();
    }
  };

  return (
    <section className="content admin roleAdminPage signupRequestsPage">
      <div className="roleAdminHeader">
        <div>
          <h2>계정 생성 요청</h2>
          <p>일반 계정과 매니저 계정 생성 요청을 승인하거나 거절합니다.</p>
        </div>
        <button
          className="roleAdminSecondaryButton signupRequestsPage__refresh"
          disabled={isLoading}
          onClick={loadRequests}
          type="button"
        >
          <RefreshCw aria-hidden="true" size={15} />
          <span>새로고침</span>
        </button>
      </div>

      {isMaster ? (
        <AdminManagerRequestCard
          managerRequests={managerRequests}
          managerRequestError={managerRequestError}
          onReview={handleReviewManagerRequest}
        />
      ) : null}

      <section className="roleAdminPanel">
        <div className="roleAdminPanel__header">
          <h3>일반 계정 생성 승인</h3>
          <span>{requests.length}건</span>
        </div>

        {isLoading && requests.length === 0 ? (
          <div className="signupRequestsPage__loading">
            <Spinner />
          </div>
        ) : (
          <div className="roleAdminTableWrap">
            <table className="roleAdminTable signupRequestsPage__table">
              <thead>
                <tr>
                  <th>요청자</th>
                  <th>회사</th>
                  <th>국가</th>
                  <th>요청일</th>
                  <th>처리</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request) => {
                  const payload = getRequestPayload(request);
                  const isProcessing = processingId === request.id;

                  return (
                    <tr key={request.id}>
                      <td>
                        <strong>{getRequestName(request)}</strong>
                        <span>{getRequestEmail(request)}</span>
                      </td>
                      <td>{payload.company_name || payload.companyName || "-"}</td>
                      <td>{payload.country || "-"}</td>
                      <td>
                        {formatRequestDate(
                          request.created_at || request.createdAt,
                        )}
                      </td>
                      <td>
                        <div className="signupRequestsPage__actions">
                          <button
                            className="roleAdminSecondaryButton"
                            disabled={Boolean(processingId)}
                            onClick={() => handleReview(request, "reject")}
                            type="button"
                          >
                            <X aria-hidden="true" size={14} />
                            <span>{isProcessing ? "처리 중" : "거절"}</span>
                          </button>
                          <button
                            className="roleAdminPrimaryButton"
                            disabled={Boolean(processingId)}
                            onClick={() => handleReview(request, "approve")}
                            type="button"
                          >
                            <Check aria-hidden="true" size={14} />
                            <span>{isProcessing ? "처리 중" : "승인"}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {requests.length === 0 ? (
                  <tr>
                    <td className="roleAdminTable__empty" colSpan={5}>
                      {errorMessage || "대기 중인 계정 생성 요청이 없습니다."}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </section>
  );
}
